import { useState } from 'react';
import { generatePDFFromHTML, downloadPDF } from '@/utils/pdf-creator';
import { toast } from '@/hooks/errors/use-toast';

interface DocumentData {
  title: string;
  content: string;
  type?: string | null;
  updated_at?: string;
}

export const useDocumentPDF = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async (document: DocumentData) => {
    setIsGenerating(true);
    
    try {
      // Create a styled HTML document with metadata header
      const htmlContent = `
        <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
          <h1 style="color: #333; border-bottom: 2px solid #4F46E5; padding-bottom: 10px; margin-bottom: 20px;">
            ${document.title}
          </h1>
          
          <div style="background: #F3F4F6; padding: 15px; border-radius: 8px; margin-bottom: 30px;">
            <p style="margin: 5px 0; color: #6B7280;">
              <strong>Document Type:</strong> ${document.type || 'Document'}
            </p>
            <p style="margin: 5px 0; color: #6B7280;">
              <strong>Last Updated:</strong> ${document.updated_at ? new Date(document.updated_at).toLocaleDateString() : 'Unknown'}
            </p>
          </div>
          
          <div style="line-height: 1.6; color: #333;">
            ${document.content || '<p>No content available</p>'}
          </div>
        </div>
      `;
      
      // Generate PDF filename - sanitize the title for use as filename
      const filename = `${document.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      
      // Generate PDF using the existing PDF generation system
      const result = await generatePDFFromHTML(htmlContent, {
        filename,
        format: 'a4',
        orientation: 'portrait',
        margin: { top: 20, right: 20, bottom: 20, left: 20 },
        quality: 0.95
      });

      if (result.success && result.blob && result.filename) {
        downloadPDF(result.blob, result.filename);
        
        toast({
          title: "PDF Downloaded Successfully",
          description: "Your document has been downloaded as a PDF file",
        });
      } else {
        throw new Error(result.error || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      
      toast({
        title: "PDF Download Failed",
        description: error instanceof Error ? error.message : "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return { generatePDF, isGenerating };
};